import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { Product } from "../../types/types";
import { buildCardEventDetail } from "../../utils/events/cardEventDetail";
import { dispatchCustomEvent } from "../../utils/events/dispatchCustomEvent";
import { raise } from "../../utils/raise";
import { useVariant } from "../VariantContext/VariantContext";
import { useProductCardMetadata } from "../ProductCardMetadataContext/ProductCardMetadataContext";
import { CardEventName, productCardEvents } from "./productCardEvents";

type DispatchCardEvent = (
  type: CardEventName,
  customDetail?: Record<string, unknown>,
) => void;

const ProductCardEventContext = createContext<DispatchCardEvent | null>(null);

type Props = {
  product: Product;
  index: number;
  eventsRef: RefObject<HTMLElement | null>;
  children: ReactNode;
};

export function ProductCardEventProvider({
  children,
  product,
  index,
  eventsRef,
}: Props): React.JSX.Element {
  const { variant } = useVariant();
  const { instanceId, consumerId } = useProductCardMetadata();
  const hasMounted = useRef(false);

  const variantId = variant?.id;
  const skuId = variant?.aliases?.skuId;

  const dispatchCardEvent = useCallback<DispatchCardEvent>(
    (type, customDetail) => {
      dispatchCustomEvent(eventsRef, type, {
        ...buildCardEventDetail({
          product,
          index,
          quantity: 1,
          instanceId,
          consumerId,
          variantId,
          skuId,
        }),
        ...customDetail,
      });
    },
    [eventsRef, product, index, instanceId, consumerId, variantId, skuId],
  );

  const latestDispatchCardEvent = useRef(dispatchCardEvent);
  latestDispatchCardEvent.current = dispatchCardEvent;

  useEffect(
    function dispatchVariantChangedEvent() {
      if (!hasMounted.current) {
        hasMounted.current = true;
        return;
      }

      if (!variantId) {
        return;
      }

      latestDispatchCardEvent.current(productCardEvents.variantChanged, {
        quantity: undefined,
      });
    },
    [variantId, skuId],
  );

  return (
    <ProductCardEventContext.Provider value={dispatchCardEvent}>
      {children}
    </ProductCardEventContext.Provider>
  );
}

export function useProductCardEvent(): DispatchCardEvent {
  return (
    useContext(ProductCardEventContext) ??
    raise("useProductCardEvent must be used within a ProductCardEventProvider")
  );
}
